// screens/marketplace/AdminCourses.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  GraduationCap,
  Plus,
  Search,
  RefreshCw,
  X,
  Pencil,
  Power,
  Trash2,
  AlertCircle,
  Save,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import {
  PageShell,
  Section,
  Button,
  Field,
  Input,
  Textarea,
  EmptyState,
  LoadingBlock,
  parseApiError,
  useTokens,
} from "./shared";
import RichTextEditor from "../../components/RichTextEditor";
import {
  createCourse,
  updateCourse,
  deactivateCourse,
} from "./coursesManageApi";
import { listCourses } from "./coursesApi";

const EMPTY = {
  title: "",
  slug: "",
  description: "",
  short_description: "",
  price: "",
  base_price_agent: "",
  cover_image: "",
  telegram_link: "",
  is_active: true,
};

export default function AdminCourses() {
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const t = useTokens();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // { mode, course?, form, submitting, errors }
  const [busyIds, setBusyIds] = useState(() => new Set());

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const data = await listCourses(authFetch, { search });
        setCourses(data);
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch, search, showToast]
  );

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setModal({
      mode: "create",
      course: null,
      form: { ...EMPTY },
      submitting: false,
      errors: {},
    });
  };

  const openEdit = (course) => {
    setModal({
      mode: "edit",
      course,
      form: {
        title: course.title || "",
        slug: course.slug || "",
        description: course.description || "",
        short_description: course.short_description || "",
        price: String(course.price || ""),
        base_price_agent: String(course.base_price_agent || ""),
        cover_image: "", // never prefill a File
        telegram_link: course.telegram_link || "",
        is_active: !!course.is_active,
      },
      submitting: false,
      errors: {},
    });
  };

  const closeModal = () => setModal(null);

  const updateField = (key, value) =>
    setModal((m) => (m ? { ...m, form: { ...m.form, [key]: value } } : m));

  const submit = async () => {
    if (!modal) return;
    const errs = {};
    if (!modal.form.title.trim()) errs.title = "Title is required";
    if (!modal.form.slug.trim()) errs.slug = "Slug is required";
    if (!modal.form.price || Number(modal.form.price) <= 0)
      errs.price = "Customer price must be greater than 0";
    if (
      !modal.form.base_price_agent ||
      Number(modal.form.base_price_agent) <= 0
    )
      errs.base_price_agent = "Agent base price must be greater than 0";
    if (modal.mode === "create" && !modal.form.telegram_link.trim())
      errs.telegram_link = "Telegram invite link is required";

    if (Object.keys(errs).length) {
      setModal((m) => ({ ...m, errors: errs }));
      showToast("Please fix the highlighted fields", "error");
      return;
    }

    setModal((m) => ({ ...m, submitting: true, errors: {} }));
    try {
      const payload = {
        title: modal.form.title.trim(),
        slug: modal.form.slug.trim(),
        description: modal.form.description,
        short_description: modal.form.short_description,
        price: modal.form.price,
        base_price_agent: modal.form.base_price_agent,
        telegram_link: modal.form.telegram_link,
        is_active: !!modal.form.is_active,
      };
      if (modal.form.cover_image instanceof File) {
        payload.cover_image = modal.form.cover_image;
      }

      if (modal.mode === "edit") {
        await updateCourse(authFetch, modal.course.id, payload);
        showToast("Course updated", "success");
      } else {
        await createCourse(authFetch, payload);
        showToast("Course created", "success");
      }
      closeModal();
      load({ silent: true });
    } catch (err) {
      showToast(err.message, "error");
      setModal((m) => (m ? { ...m, submitting: false } : m));
    }
  };

  const toggleActive = async (course) => {
    setBusyIds((s) => new Set(s).add(course.id));
    try {
      if (course.is_active) {
        await deactivateCourse(authFetch, course.id);
        showToast("Course deactivated", "success");
      } else {
        await updateCourse(authFetch, course.id, { is_active: true });
        showToast("Course activated", "success");
      }
      load({ silent: true });
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setBusyIds((s) => {
        const n = new Set(s);
        n.delete(course.id);
        return n;
      });
    }
  };

  return (
    <PageShell
      eyebrow="Courses"
      title="Course catalog"
      subtitle="Create and manage digital courses. Each course is bought once and unlocks a Telegram invite."
      actions={
        <>
          <Button
            variant="secondary"
            icon={RefreshCw}
            onClick={() => load({ silent: true })}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button icon={Plus} onClick={openCreate}>
            New course
          </Button>
        </>
      }
    >
      <Section
        title="Courses"
        icon={GraduationCap}
        chip={`${courses.length}`}
      >
        <div style={{ marginBottom: 14, position: "relative", maxWidth: 380 }}>
          <Search
            size={15}
            color={t.muted}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          />
          <Input
            placeholder="Search courses…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
        </div>

        {loading ? (
          <LoadingBlock label="Loading courses…" />
        ) : courses.length === 0 ? (
          <EmptyState
            icon={GraduationCap}
            title={search ? "No courses match" : "No courses yet"}
            description={
              search
                ? "Try a different search term."
                : "Create your first course to start selling."
            }
            action={
              !search && (
                <Button icon={Plus} onClick={openCreate}>
                  New course
                </Button>
              )
            }
          />
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {courses.map((course) => {
              const busy = busyIds.has(course.id);
              return (
                <div
                  key={course.id}
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 14,
                    padding: 14,
                    borderRadius: 12,
                    border: `1px solid ${t.border}`,
                    background: t.surfaceRaised,
                    opacity: busy ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      width: 64,
                      height: 44,
                      borderRadius: 8,
                      overflow: "hidden",
                      background: t.subtle,
                      flexShrink: 0,
                    }}
                  >
                    {course.cover_image_url ? (
                      <img
                        src={course.cover_image_url}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: t.muted,
                        }}
                      >
                        <GraduationCap size={18} />
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: t.heading,
                      }}
                    >
                      {course.title}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: t.muted,
                        marginTop: 2,
                      }}
                    >
                      ₵{Number(course.price).toFixed(2)} · agent ₵
                      {Number(course.base_price_agent).toFixed(2)}
                    </div>
                  </div>

                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 9px",
                      borderRadius: 999,
                      background: course.is_active
                        ? t.successBg
                        : t.subtle,
                      color: course.is_active ? t.success : t.muted,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                    }}
                  >
                    {course.is_active ? "Active" : "Inactive"}
                  </span>

                  <Button
                    size="sm"
                    variant="secondary"
                    icon={Pencil}
                    onClick={() => openEdit(course)}
                    disabled={busy}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant={course.is_active ? "dangerGhost" : "secondary"}
                    icon={Power}
                    onClick={() => toggleActive(course)}
                    disabled={busy}
                  >
                    {course.is_active ? "Deactivate" : "Activate"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {modal && (
        <CourseEditorModal
          modal={modal}
          onUpdateField={updateField}
          onClose={closeModal}
          onSubmit={submit}
          t={t}
        />
      )}
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════
   Modal
   ══════════════════════════════════════════════════════════ */

const CourseEditorModal = ({ modal, onUpdateField, onClose, onSubmit, t }) => {
  const isEdit = modal.mode === "edit";
  const errors = modal.errors || {};

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: t.scrim,
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 1000,
        padding: "24px 16px",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          background: t.surfaceRaised,
          border: `1px solid ${t.border}`,
          borderRadius: 16,
          width: "100%",
          maxWidth: 760,
          padding: 24,
          boxShadow: `0 24px 64px ${t.shadowStrong}`,
          boxSizing: "border-box",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: t.heading,
                letterSpacing: "-0.01em",
              }}
            >
              {isEdit ? "Edit course" : "New course"}
            </div>
            <div style={{ fontSize: 12.5, color: t.muted, marginTop: 2 }}>
              {isEdit
                ? "Update pricing, description, or the Telegram invite."
                : "Admins create courses. Buyers pay once and receive the Telegram invite."}
            </div>
          </div>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} />
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div style={styles.grid(2)}>
            <Field label="Title" error={errors.title}>
              <Input
                value={modal.form.title}
                onChange={(e) => onUpdateField("title", e.target.value)}
                placeholder="e.g. Forex Basics"
              />
            </Field>
            <Field label="URL slug" error={errors.slug}>
              <Input
                value={modal.form.slug}
                onChange={(e) => onUpdateField("slug", e.target.value)}
                placeholder="forex-basics"
              />
            </Field>

            <Field label="Customer price (₵)" error={errors.price}>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={modal.form.price}
                onChange={(e) => onUpdateField("price", e.target.value)}
                placeholder="150.00"
              />
            </Field>
            <Field
              label="Agent base price (₵)"
              error={errors.base_price_agent}
            >
              <Input
                type="number"
                min="0"
                step="0.01"
                value={modal.form.base_price_agent}
                onChange={(e) =>
                  onUpdateField("base_price_agent", e.target.value)
                }
                placeholder="120.00"
              />
            </Field>

            <Field label="Short description" hint="Shown on the course card">
              <Input
                value={modal.form.short_description}
                onChange={(e) =>
                  onUpdateField("short_description", e.target.value)
                }
                placeholder="Beginner forex course"
              />
            </Field>

            <Field
              label="Telegram invite link"
              error={errors.telegram_link}
              hint={
                isEdit
                  ? "Leave blank to keep the current invite link."
                  : "Buyers see this after payment confirms."
              }
            >
              <Input
                value={modal.form.telegram_link}
                onChange={(e) =>
                  onUpdateField("telegram_link", e.target.value)
                }
                placeholder="https://t.me/+abc123"
              />
            </Field>

            <Field
              label="Cover image"
              hint={isEdit ? "Leave blank to keep the current cover" : "16:9 recommended"}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) =>
                  onUpdateField("cover_image", e.target.files?.[0] || "")
                }
                style={{
                  width: "100%",
                  padding: "8px 0",
                  fontSize: 13,
                  color: t.text,
                }}
              />
            </Field>

            <Field label="Status">
              <select
                value={modal.form.is_active ? "active" : "draft"}
                onChange={(e) =>
                  onUpdateField("is_active", e.target.value === "active")
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1px solid ${t.border}`,
                  background: t.raisedBg,
                  color: t.text,
                  fontSize: 14,
                  outline: "none",
                  fontFamily: "inherit",
                }}
              >
                <option value="active">Active (visible to buyers)</option>
                <option value="draft">Draft (hidden)</option>
              </select>
            </Field>
          </div>

          {/* Rich description */}
          <div style={{ marginTop: 18 }}>
            <div
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: t.text,
                marginBottom: 6,
              }}
            >
              Full description
            </div>
            <RichTextEditor
              value={modal.form.description}
              onChange={(html) => onUpdateField("description", html)}
              placeholder="Write what the course covers. Use headings, lists, and links."
              minHeight={220}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 22,
              paddingTop: 18,
              borderTop: `1px solid ${t.separator || t.border}`,
            }}
          >
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              icon={Save}
              disabled={modal.submitting}
            >
              {modal.submitting
                ? "Saving…"
                : isEdit
                ? "Save changes"
                : "Create course"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  grid: (cols) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gap: 14,
  }),
};